<template>
  <div class="min-h-screen">
    <!-- Header -->
    <AppHeader />
    
    <!-- Page Hero -->
    <section class="relative overflow-hidden bg-gray-50 section-padding">
      <!-- Background Elements matching Hero -->
      <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div class="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/40 blur-3xl"></div>
        <div class="absolute -bottom-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-secondary-200/40 blur-3xl"></div>
        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] rounded-full bg-purple-200/30 blur-3xl"></div>
      </div>

      <div class="container-custom relative z-10">
        <div v-if="loading" class="text-center">
          <SkeletonLoader type="title" width="400px" class="mx-auto mb-4" />
          <SkeletonLoader type="text" width="600px" class="mx-auto" />
        </div>
        <div v-else class="text-center">
          <div class="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary-100 text-primary-600 text-sm font-medium mb-6 shadow-lg">
            <DocumentIcon class="w-4 h-4 mr-2" />
            Legal Agreement
          </div>
          <h1 class="font-heading text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {{ pageData?.title || 'Terms of Service' }}
          </h1>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            {{ pageData?.meta_description || 'Read our terms and conditions for using our services.' }}
          </p>
        </div>
      </div>
    </section>
    
    <!-- Content Section -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div class="max-w-4xl mx-auto">
          <div v-if="loading" class="space-y-4">
            <SkeletonLoader v-for="i in 8" :key="i" type="text" />
          </div>
          
          <div v-else-if="error" class="text-center py-16">
            <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-2">Failed to Load Content</h3>
            <p class="text-gray-600 mb-6">{{ error }}</p>
            <button @click="loadContent" class="btn-primary">
              Try Again
            </button>
          </div>
          
          <div v-else class="legal-content prose prose-lg max-w-none" v-html="pageData?.content"></div>
        </div>
      </div>
    </section>
    
    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useLegalPagesStore } from '@/stores/legalPages'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'

const legalPagesStore = useLegalPagesStore()

const loading = ref(true)
const error = ref(null)
const pageData = ref(null)

const loadContent = async () => {
  loading.value = true
  error.value = null
  
  try {
    const data = await legalPagesStore.fetchLegalPage('terms-of-service')
    if (data) {
      pageData.value = data
    } else {
      error.value = 'Terms of Service not found'
    }
  } catch (err) {
    error.value = err.message || 'Failed to load Terms of Service'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadContent()
})
</script>

<script>
export default {
  components: {
    DocumentIcon: {
      template: `
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
        </svg>
      `
    }
  }
}
</script>

<style scoped>
.legal-content :deep(h2) {
  @apply text-2xl font-bold text-gray-900 mt-12 mb-6 pb-3 border-b border-gray-200;
}

.legal-content :deep(h3) {
  @apply text-xl font-semibold text-gray-900 mt-8 mb-4;
}

.legal-content :deep(p) {
  @apply text-gray-700 mb-4 leading-relaxed;
}

.legal-content :deep(ul) {
  @apply list-disc list-inside mb-6 space-y-2 text-gray-700;
}

.legal-content :deep(ul li) {
  @apply ml-4;
}

.legal-content :deep(a) {
  @apply text-primary-600 hover:text-primary-700 underline;
}

.legal-content :deep(.last-updated) {
  @apply text-sm text-gray-500 italic mb-8 pb-4 border-b border-gray-200;
}

.legal-content :deep(.contact-info) {
  @apply bg-gray-50 p-6 rounded-lg border border-gray-200 my-6;
}

.legal-content :deep(.contact-info p) {
  @apply mb-0;
}
</style>
